/**
 * Retour d'un `onSend` de composer : `false` = envoi refusé, le brouillon doit
 * rester à l'écran. Toute autre valeur (y compris `void`, contrat historique des
 * hôtes qui ne refusent jamais) vaut acceptation.
 */
export type ComposerSendResult = boolean | void;

/** Un `onSend` peut trancher tout de suite ou après une ouverture de socket / un téléversement. */
export type ComposerSendOutcome = ComposerSendResult | PromiseLike<ComposerSendResult>;

function isPromiseLike(value: ComposerSendOutcome): value is PromiseLike<ComposerSendResult> {
  return typeof value === 'object' && value !== null && typeof value.then === 'function';
}

/**
 * Envoie le brouillon et ne le retire du composer que si l'hôte l'a accepté.
 *
 * Pourquoi : le composer vidait le champ et les pièces jointes sans lire le
 * retour de `onSend`. Un envoi refusé (réponse ou compaction en cours dans le
 * dock dossier, pool saturé, téléversement échoué…) faisait disparaître le texte
 * de l'avocat sans retour possible.
 *
 * - Refus synchrone (`false`) : le champ n'est jamais vidé.
 * - Verdict asynchrone : le champ est vidé tout de suite — l'hôte affiche déjà la
 *   bulle du message, le laisser aussi dans le champ le montrerait deux fois — puis
 *   le brouillon est rendu si l'envoi est refusé ou échoue.
 */
export function submitComposerDraft({
  send,
  clearDraft,
  restoreDraft,
}: {
  send: () => ComposerSendOutcome;
  clearDraft: () => void;
  restoreDraft: () => void;
}): void {
  const outcome = send();
  if (outcome === false) return;
  clearDraft();
  if (!isPromiseLike(outcome)) return;
  outcome.then(
    (accepted) => {
      if (accepted === false) restoreDraft();
    },
    () => restoreDraft(),
  );
}

/**
 * Pièces jointes du composeur une fois des fichiers RENDUS : ceux d'un envoi
 * refusé, ou ceux qu'un message n'a pas pu emporter (#2066). Ils reviennent en
 * tête, à côté de ce que l'avocat a joint entre-temps — un téléversement de
 * 50 fichiers dure assez pour qu'il prépare la suite.
 *
 * Un fichier déjà présent n'est pas doublé (comparaison par identité, deux
 * pièces pouvant porter le même nom) : rendre deux fois les mêmes fichiers est
 * sans effet, et la liste courante est alors rendue telle quelle.
 */
export function mergeReturnedFiles(current: File[], returned: readonly File[]): File[] {
  const missing = returned.filter((file) => !current.includes(file));
  // `current` est l'état React du composeur : le rendre inchangé évite un rendu.
  return missing.length === 0 ? current : [...missing, ...current];
}
